import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePhoto from '../../components/ProfilePhoto';
import VersionPill from '../../components/VersionPill';
import {
  ChartBar, Users, Truck, MapPin, Package, ListChecks,
  Clock, GasPump, Polygon, FileText, SignOut, List,
  Scales, Certificate, UserList, Timer, Gear, Bell, NavigationArrow, Trophy, Shield, Gauge, PersonSimple, Storefront, DeviceMobile, Calendar, Globe, CurrencyInr
} from '@phosphor-icons/react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: ChartBar, end: true },
  { path: '/admin/scorecard', label: 'Scorecard', icon: Trophy },
  { path: '/admin/live-map', label: 'Live Tracking', icon: NavigationArrow },
  { path: '/admin/pilots', label: 'Pilots', icon: Users },
  { path: '/admin/vehicles', label: 'Vehicles', icon: Truck },
  { path: '/admin/vehicle-capacity', label: 'Vehicle Capacity', icon: Scales },
  { path: '/admin/vehicle-control', label: 'Vehicle Control', icon: Shield },
  { path: '/admin/suppliers', label: 'Suppliers', icon: MapPin },
  { path: '/admin/supplier-coord-health', label: 'Supplier Coord Health', icon: MapPin },
  { path: '/admin/pickup-users', label: 'Pickup Users', icon: Storefront },
  { path: '/admin/staff', label: 'Booking', icon: UserList },
  { path: '/admin/items', label: 'Items', icon: Package },
  { path: '/admin/uom', label: 'UOM', icon: Scales },
  { path: '/admin/license-types', label: 'License Types', icon: Certificate },
  { path: '/admin/loadmen', label: 'Helpers', icon: PersonSimple },
  { path: '/admin/helper-contractors', label: 'Helper Contractors', icon: UserList },
  { path: '/admin/loadman-attendance', label: 'Helper Attendance', icon: ListChecks },
  { path: '/admin/loadmen-usage', label: 'Helpers Usage', icon: Scales },
  { path: '/admin/trips', label: 'Trip Monitoring', icon: ListChecks },
  { path: '/admin/pilot-trips', label: 'Pilot Trips', icon: Users },
  { path: '/admin/attendance', label: 'Attendance', icon: Clock },
  { path: '/admin/breaks', label: 'Break Approvals', icon: Timer },
  { path: '/admin/fuel', label: 'Fuel Reports', icon: GasPump },
  { path: '/admin/fuel-efficiency', label: 'Fuel Efficiency', icon: Gauge },
  { path: '/admin/waiting-rules', label: 'Waiting Rules', icon: Gear },
  { path: '/admin/pickup-drop-timings', label: 'Pickup/Drop Timings', icon: Timer },
  { path: '/admin/translations', label: 'Tamil Translations', icon: Globe },
  { path: '/admin/geofences', label: 'Geofences (Map)', icon: Polygon },
  { path: '/admin/halt-locations', label: 'Halt Locations', icon: MapPin },
  { path: '/admin/fuel-suppliers', label: 'Fuel Suppliers', icon: GasPump },
  { path: '/admin/contractor-helper-report', label: 'Contractor Report', icon: FileText },
  { path: '/admin/reports', label: 'Reports', icon: FileText },
  { path: '/admin/daily-report', label: 'Daily Report', icon: Calendar },
  { path: '/admin/admin-users', label: 'Admin Users', icon: Shield },
  { path: '/admin/app-distribution', label: 'App Distribution', icon: DeviceMobile },
  { path: '/admin/google-api', label: 'Google API', icon: CurrencyInr },
  { path: '/admin/settings', label: 'Settings', icon: Gear },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] text-white flex flex-col transform transition-transform duration-150 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b border-white/10">
          <img src="/carloo-logo.png" alt="Carloo" className="h-8 invert" />
          <p className="text-white/40 text-xs uppercase tracking-widest mt-1">Control Panel</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-[#002FA7] text-white font-medium'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <item.icon size={18} weight="regular" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <ProfilePhoto currentPhoto={user?.profile_photo} onPhotoUpdate={() => {}} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-2 py-2 text-sm text-white/50 hover:text-[#FF3333] transition-colors w-full"
          >
            <SignOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-[#F7F7F7]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E5E5E5] px-6 py-4 flex items-center justify-between">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 hover:bg-[#F3F3F3] transition-colors"
          >
            <List size={20} />
          </button>
          <div className="flex items-center gap-3">
            {/* Phase 76u — version pill so support knows which build the
                admin is on without asking. */}
            <VersionPill />
            <button className="relative p-2 hover:bg-[#F3F3F3] transition-colors" data-testid="notifications-bell">
              <Bell size={20} className="text-[#666]" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#FF3333] rounded-full" />
            </button>
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
